import React, { useState, useRef, useEffect } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import DayColumn from "./day-column";
import {
  CALENDAR_END_HOUR,
  CALENDAR_START_HOUR,
  COLUMN_TOTAL_HEIGHT,
  HOUR_HEIGHT,
  PIXELS_PER_MINUTE,
  TIME_GUTTER_WIDTH,
} from "@/constants/scheduleConstant";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";

import { useColorScheme } from "@/hooks/use-color-scheme";
import { Colors } from "@/constants/theme";
import { ClassSchedule } from "@/hooks/use-calendar";
import { DayOfWeek } from "@/types/dayOfWeek";
import { router } from "expo-router";
import { buildClassDirectionsNavigationParams } from "@/utils/classMapDestination";

const HOURS = Array.from(
  { length: CALENDAR_END_HOUR - CALENDAR_START_HOUR },
  (_, i) => CALENDAR_START_HOUR + i,
);

interface WeeklyCalendarBodyProps {
  weekStartDate: Date;
  classes: ClassSchedule[];
  colorMap: Map<string, string>;
  onClassPress: (classInfo: ClassSchedule) => void;
}

function getCurrentTimeY(): number {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  return currentMinutes * PIXELS_PER_MINUTE;
}

export function getNextClass(classes: ClassSchedule[]): ClassSchedule | undefined {
  const now = new Date();
  const todayWeekday = now.getDay() as DayOfWeek;

  const classesToday = classes.filter((cls) => {
    return DayOfWeek.fromShortString(cls.DAY_OF_WEEK) === todayWeekday;
  });

  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  let nextClass = undefined;

  for (const cls of classesToday) {
    let startTimeInMinutes =
      Number(`${cls.START_HOURS}`) * 60 + Number(`${cls.START_MINUTES}`);
    let endTimeInMinutes = Number(`${cls.END_HOURS}`) * 60 + Number(`${cls.END_MINUTES}`);

    if (endTimeInMinutes - currentMinutes <= 0) continue; // Class has finished
    if (currentMinutes - startTimeInMinutes > 30) continue; // More than 30 minutes since class has started

    if (nextClass === undefined) {
      nextClass = cls;
      continue;
    }

    let thisDelta = endTimeInMinutes - currentMinutes;
    let smallestDeltaSoFar =
      Number(nextClass.END_HOURS) * 60 + Number(nextClass.END_MINUTES) - currentMinutes;

    if (thisDelta < smallestDeltaSoFar) {
      nextClass = cls;
    }
  }

  return nextClass;
}

export default function WeeklyCalendarBody({
  weekStartDate,
  classes,
  colorMap,
  onClassPress,
}: Readonly<WeeklyCalendarBodyProps>) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme];
  const { width: windowWidth } = useWindowDimensions();
  const isCompactWebLayout = Platform.OS === "web" && windowWidth < 720;
  const nextClassButtonMaxWidth =
    Platform.OS === "web" ? Math.max(Math.min(windowWidth - 24, 360), 0) : undefined;

  // Time state for horizontal time bar
  const [currentTimeY, setCurrentTimeY] = useState(() => getCurrentTimeY());
  const [nextClass] = useState(() => getNextClass(classes));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeY(getCurrentTimeY());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const today = new Date();

  // Defaults the schedule to display from 08:00 onwards, rather than from 00:00 onwards
  const scrollViewRef = useRef<ScrollView>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollViewRef.current?.scrollTo({
        y: 8 * HOUR_HEIGHT,
        animated: false,
      });
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStartDate);
    d.setDate(weekStartDate.getDate() + i);
    return d;
  });

  const isToday = (date: Date) =>
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  async function handleNextClassPress() {
    if (!nextClass) return;
    const params = await buildClassDirectionsNavigationParams(nextClass);
    router.navigate({
      pathname: "/",
      params,
    });
  }

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.weeklyCalendarBody.backgroundColor },
      ]}
    >
      <View
        style={[
          styles.headerRow,
          {
            backgroundColor: theme.weeklyCalendarBody.backgroundColor,
            borderBottomColor: theme.weeklyCalendarBody.borderColor,
          },
        ]}
      >
        <View style={styles.timeGutterSpacer} />

        {weekDates.map((date, i) => (
          <View
            key={date.toLocaleDateString("en-US", { weekday: "short" })}
            style={[
              styles.dayHeader,
              { borderLeftColor: theme.weeklyCalendarBody.borderColor },
              isToday(date) && { backgroundColor: theme.weeklyCalendarBody.todayColor },
            ]}
          >
            <Text
              style={[
                styles.dayLabel,
                { color: theme.weeklyCalendarBody.dayAndTimeLabel },
                isToday(date) && { color: theme.tint },
              ]}
            >
              {date.toLocaleDateString("en-US", { weekday: "short" })}
            </Text>
            <View
              style={[
                styles.dateCircle,
                isToday(date) && { backgroundColor: theme.tint },
              ]}
            >
              <Text
                style={[
                  styles.dateNumber,
                  { color: theme.weeklyCalendarBody.dateNumber },
                  isToday(date) && { color: theme.weeklyCalendarBody.dateNumberToday },
                ]}
              >
                {date.getDate()}
              </Text>
            </View>
          </View>
        ))}
      </View>

      <ScrollView
        ref={scrollViewRef}
        style={styles.verticalScroll}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.bodyRow}>
          <View
            style={[
              styles.timeGutter,
              { backgroundColor: theme.weeklyCalendarBody.backgroundColor },
              { height: COLUMN_TOTAL_HEIGHT },
            ]}
          >
            {HOURS.map((hour) => (
              <View
                key={hour}
                style={[styles.timeLabelRow, { top: hour * HOUR_HEIGHT - 8 }]}
              >
                <Text
                  style={[
                    styles.timeLabelText,
                    { color: theme.weeklyCalendarBody.dayAndTimeLabel },
                  ]}
                >
                  {String(hour).padStart(2, "0")}:00
                </Text>
              </View>
            ))}
          </View>

          <View style={styles.columnsContainer}>
            <View
              style={[
                StyleSheet.absoluteFillObject,
                { height: COLUMN_TOTAL_HEIGHT, pointerEvents: "none" },
              ]}
            >
              {HOURS.map((hour) => (
                <View
                  key={hour}
                  style={[
                    styles.hourLine,
                    { backgroundColor: theme.weeklyCalendarBody.hourLineColor },
                    { top: hour * HOUR_HEIGHT },
                  ]}
                />
              ))}
            </View>

            <View
              style={[
                StyleSheet.absoluteFillObject,
                { height: COLUMN_TOTAL_HEIGHT, pointerEvents: "none" },
              ]}
            >
              {weekDates.some((date) => isToday(date)) && (
                <View
                  style={[
                    styles.currentTimeLine,
                    { top: currentTimeY, pointerEvents: "none" },
                  ]}
                >
                  <View
                    style={[
                      styles.currentTimeDot,
                      { backgroundColor: theme.weeklyCalendarBody.timeDotColor },
                    ]}
                  />
                  <View
                    style={[
                      styles.currentTimeBar,
                      { backgroundColor: theme.weeklyCalendarBody.timeDotColor },
                    ]}
                  />
                </View>
              )}
            </View>

            {weekDates.map((date, i) => (
              <DayColumn
                key={date.toLocaleDateString("en-US", { weekday: "short" }).toLowerCase()}
                dayIndex={i}
                isToday={isToday(date)}
                classes={classes.filter((cls) =>
                  cls.DAY_OF_WEEK.includes(
                    date.toLocaleDateString("en-US", { weekday: "short" }).toLowerCase(),
                  ),
                )}
                colorMap={colorMap}
                onClassPress={onClassPress}
              />
            ))}
          </View>
        </View>
      </ScrollView>

      <View
        style={[
          styles.nextClassButtonContainer,
          isCompactWebLayout && styles.nextClassButtonContainerCompact,
        ]}
      >
        <Pressable
          onPress={() => {
            handleNextClassPress();
          }}
          style={[
            styles.nextClassButton,
            nextClassButtonMaxWidth !== undefined && {
              maxWidth: nextClassButtonMaxWidth,
            },
            { backgroundColor: theme.weeklyCalendarBody.nextClassButtonColor },
          ]}
          accessibilityLabel="Jump to next class"
        >
          <View style={styles.nextClassButtonCopy}>
            <Text
              testID="next-class"
              style={[
                styles.nextClassButtonTitle,
                { color: theme.weeklyCalendarBody.nextClassButtonText },
              ]}
            >
              Next Class:{" "}
            </Text>
            <Text
              style={[
                styles.nextClassButtonText,
                { color: theme.weeklyCalendarBody.nextClassButtonText },
              ]}
            >
              {nextClass
                ? `${nextClass.SUBJECT} ${nextClass.CATALOG_NBR} at ${nextClass.START_HOURS}:${String(nextClass.START_MINUTES).padStart(2, "0")}`
                : "No more classes today"}
            </Text>
          </View>
          <MaterialIcons
            name="location-pin"
            size={isCompactWebLayout ? 34 : 40}
            color={theme.weeklyCalendarBody.nextClassButtonText}
          />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerRow: {
    flexDirection: "row",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  timeGutterSpacer: {
    width: TIME_GUTTER_WIDTH,
  },
  dayHeader: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 8,
    borderLeftWidth: StyleSheet.hairlineWidth,
    minWidth: 0,
  },
  dayLabel: {
    fontSize: 10,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  dateCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 3,
  },
  dateNumber: {
    fontSize: 12,
    fontWeight: "600",
  },
  verticalScroll: {
    flex: 1,
  },
  bodyRow: {
    flexDirection: "row",
    width: "100%",
    minWidth: 0,
  },
  timeGutter: {
    width: TIME_GUTTER_WIDTH,
    position: "relative",
  },
  timeLabelRow: {
    position: "absolute",
    right: 6,
    alignItems: "flex-end",
  },
  timeLabelText: {
    fontSize: 9,
    fontVariant: ["tabular-nums"],
  },
  columnsContainer: {
    flex: 1,
    flexDirection: "row",
    position: "relative",
    minWidth: 0,
  },
  hourLine: {
    position: "absolute",
    left: 0,
    right: 0,
    height: StyleSheet.hairlineWidth,
  },
  currentTimeLine: {
    position: "absolute",
    left: 0,
    right: 0,
    flexDirection: "row",
    alignItems: "center",
    zIndex: 10,
  },
  currentTimeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginLeft: -4,
  },
  currentTimeBar: {
    flex: 1,
    height: 1.5,
  },
  nextClassButtonContainer: {
    position: "absolute",
    right: 16,
    bottom: 16,
    zIndex: 20,
    elevation: 8,
  },
  nextClassButtonContainerCompact: {
    right: 12,
    left: 12,
    bottom: 12,
  },
  nextClassButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    padding: 10,
    opacity: 0.8,
    maxWidth: "100%",
  },
  nextClassButtonCopy: {
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  nextClassButtonTitle: {
    fontWeight: "700",
    fontSize: 15,
    lineHeight: 20,
    marginLeft: 4,
  },
  nextClassButtonText: {
    fontWeight: "400",
    fontSize: 15,
    lineHeight: 20,
    marginLeft: 4,
  },
});
